import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PageHeader    from '../components/ui/PageHeader';
import Card          from '../components/ui/Card';
import Badge         from '../components/ui/Badge';
import TabBar        from '../components/ui/TabBar';
import ProgressBar   from '../components/ui/ProgressBar';
import KpiRow        from '../components/ui/KpiRow';
import DataTable     from '../components/ui/DataTable';
import BarChart      from '../components/charts/BarChart';
import Alert         from '../components/ui/Alert';
import { statusColor } from '../utils/helpers';
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";
import { FiRefreshCw } from "react-icons/fi";
import './Reports.css';

const TABS = [
  { id: 'summary',    label: 'Summary'         },
  { id: 'organiser',  label: 'Organiser-wise'  },
  { id: 'village',    label: 'Village-wise'    },
  { id: 'investment', label: 'Inv. vs Return'  },
];

const ORG_COLUMNS = [
  { key: 'organiser_name', label: 'Organiser',  render: (v) => <strong style={{ color: 'var(--text)' }}>{v}</strong> },
  { key: 'villages',       label: 'Villages'    },
  { key: 'farmers',        label: 'Farmers'     },
  { key: 'seeds_given',    label: 'Seeds Given',   render: (v) => <span style={{ fontFamily: 'var(--font-mono)' }}>{v}</span> },
  { key: 'collected',      label: 'Collected',     render: (v) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{v}</span> },
  { key: 'qc_pass',        label: 'QC Pass',       render: (v) => <span style={{ color: 'var(--sky)' }}>{v}</span> },
  { key: 'efficiency',     label: 'Efficiency',    render: (v) => {
    const num = Number(v) || 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ProgressBar value={num} variant={num < 65 ? 'gold' : ''} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{num}%</span>
      </div>
    );
  }},
];

const VIL_COLUMNS = [
  { key: 'village',    label: 'Village',    render: (v) => <><span>🏘</span> <strong style={{ color: 'var(--text)' }}>{v}</strong></> },
  { key: 'organiser',  label: 'Organiser'   },
  { key: 'farmers',    label: 'Farmers'     },
  { key: 'seeds',      label: 'Seeds',      render: (v) => <span style={{ fontFamily: 'var(--font-mono)' }}>{v}</span> },
  { key: 'collected',  label: 'Collected',  render: (v) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{v}</span> },
  { key: 'status',     label: 'Status',     render: (v) => <Badge color={v === 'Collection' ? 'green' : 'gold'}>{v}</Badge> },
];

export default function Reports() {
  const [tab, setTab] = useState('summary');
  const { refreshToken } = useAuth();
  
  const token = sessionStorage.getItem("maaze_token");
  const API = "https://maize.norissolutions.in/Php/public/api";

  const [seasonalProgress, setSeasonalProgress] = useState(null);
  const [investmentReturn, setInvestmentReturn] = useState(null);
  const [organiserData, setOrganiserData] = useState([]);
  const [villageData, setVillageData] = useState([]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [spRes, irRes, orgRes, vilRes] = await Promise.all([
          axios.get(`${API}/reports/seasonal-progress`, { headers }),
          axios.get(`${API}/reports/investment-return`, { headers }),
          axios.get(`${API}/reports/organiser-report`, { headers }),
          axios.get(`${API}/reports/village-report`, { headers })
        ]);

        if (spRes.data?.data) setSeasonalProgress(spRes.data.data);
        if (irRes.data?.data) setInvestmentReturn(irRes.data.data);
        if (orgRes.data?.data) setOrganiserData(orgRes.data.data);
        if (vilRes.data?.data) setVillageData(vilRes.data.data);

      } catch (err) {
        console.error("Failed to fetch reports:", err);
      }
    };
    
    fetchReports();
  }, [token]);

  const KPI = investmentReturn ? [
    { value: `₹${(investmentReturn.investment / 100000).toFixed(1)}L`, label: 'Investment' },
    { value: `₹${(investmentReturn.expenses / 100000).toFixed(1)}L`, label: 'Expenses' },
    { value: `₹${(investmentReturn.net / 100000).toFixed(1)}L`, label: 'Net Balance' },
    { value: `₹${(investmentReturn.returns / 100000).toFixed(1)}L`, label: 'Returns' },
  ] : [];

  let PROGRESS_ITEMS = [];
  if (seasonalProgress) {
    PROGRESS_ITEMS = [
      { 
        label: 'Seeds Purchased', 
        val: seasonalProgress.seeds_purchased?.current || 0, 
        max: seasonalProgress.seeds_purchased?.target || 0, 
        variant: '' 
      },
      { 
        label: 'Seeds Distributed', 
        val: seasonalProgress.seeds_distributed?.current || 0, 
        max: seasonalProgress.seeds_distributed?.target || 0, 
        variant: 'sky' 
      },
      { 
        label: 'Crop Collected', 
        val: seasonalProgress.crop_collected?.current || 0, 
        max: seasonalProgress.crop_collected?.target || 0, 
        variant: 'gold' 
      },
      { 
        label: 'Quality Approved', 
        val: seasonalProgress.quality_approved?.current || 0, 
        max: seasonalProgress.quality_approved?.target || 0, 
        variant: '' 
      },
      { 
        label: 'Factory Processed', 
        val: seasonalProgress.factory_processed?.current || 0, 
        max: seasonalProgress.factory_processed?.target || 0, 
        variant: 'sky' 
      },
    ];
  }

  let chartData = [];
  if (investmentReturn) {
    chartData = [
      { label: 'Investment', val: investmentReturn.investment / 100000, color: 'var(--accent)' },
      { label: 'Expenses',   val: investmentReturn.expenses / 100000, color: 'var(--gold)'   },
      { label: 'Returns',    val: investmentReturn.returns / 100000,  color: 'var(--sky)'    },
      { label: 'Net',        val: investmentReturn.net / 100000, color: 'var(--accent)' },
    ];
  }

  const INV_ROWS = investmentReturn ? [
    { cat: 'Total Investment', amt: Number(investmentReturn.investment || 0).toLocaleString(), notes: '', highlight: false },
    { cat: 'Total Expenses', amt: Number(investmentReturn.expenses || 0).toLocaleString(), notes: '', highlight: true },
    { cat: `Returns (Client: ${investmentReturn.client})`, amt: Number(investmentReturn.returns || 0).toLocaleString(), notes: `Finished Goods: ${investmentReturn.finished_goods_kg} kg`, highlight: true },
    { cat: 'Net Profit/Loss', amt: Number(investmentReturn.net || 0).toLocaleString(), notes: '', highlight: 'profit' },
  ] : [];

  return (
    <div>
      <PageHeader title="Reports" subtitle="Comprehensive analytics and business insights">
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'summary' && (
        <>
          <KpiRow items={KPI} />
          <div className="grid-2">
            <Card title="Seasonal Progress">
              {PROGRESS_ITEMS.map((p) => {
                const max = p.max || 1; 
                const pct = (p.val / max) * 100;
                return (
                  <div className="progress-row" key={p.label}>
                    <div className="progress-row__header">
                      <span>{p.label}</span>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{Number(p.val || 0).toLocaleString()} / {Number(p.max || 0).toLocaleString()} kg</span>
                    </div>
                    <ProgressBar value={pct} variant={p.variant} />
                  </div>
                )
              })}
            </Card>
            <Card title="Investment vs Return">
              <BarChart data={chartData} />
              {investmentReturn && (
                <Alert variant="info" style={{ marginTop: 12 }}>
                  📊 Finished goods ({investmentReturn.finished_goods_kg || 0} kg) estimated return pending delivery to {investmentReturn.client || 'Client'}.
                </Alert>
              )}
            </Card>
          </div>
        </>
      )}

      {tab === 'organiser' && (
        <Card title="Organiser-wise Report" noPad>
          <DataTable columns={ORG_COLUMNS} rows={organiserData} />
        </Card>
      )}

      {tab === 'village' && (
        <Card title="Village-wise Report" noPad>
          <DataTable columns={VIL_COLUMNS} rows={villageData} />
        </Card>
      )}

      {tab === 'investment' && (
        <Card title="Investment vs Return Analysis">
          <div className="inv-table-wrap">
            <table className="inv-table">
              <thead><tr><th>Category</th><th>Amount (₹)</th><th>Notes</th></tr></thead>
              <tbody>
                {INV_ROWS.map((r, i) => (
                  <tr key={i} className={r.highlight === 'profit' ? 'inv-table__row--profit' : r.highlight ? 'inv-table__row--total' : ''}>
                    <td>{r.cat}</td>
                    <td className="inv-table__amt">₹{r.amt}</td>
                    <td className="inv-table__notes">{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
